import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, take, tap } from 'rxjs/operators';
import { RestService } from '../../../shared/services/rest.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import { TranslocoService } from '@jsverse/transloco';
import { FoundReportPreviewPayload } from '../models/found-report-preview-payload.model';
import { PickupReportPreviewPayload } from '../models/pickup-report-preview-payload.model';
import { SecuringReportPreviewPayload } from '../models/securing-report-preview-payload.model';
import { AnimalOriginRepository } from './animal-origin.repository';
import { AnimalWhereaboutsRepository } from './animal-whereabouts.repository';

export interface FoundReportPreviewResponse {
  reportPdfPath: string | null;
}

export interface SendFoundReportPayload {
  recipientContactIds: number[];
  recipientCustoms: string[];
  mediaIds: number[];
  authorityInformation?: string | null;
}

export interface SendFoundReportBundleItem {
  animalOriginId: number;
  mediaIds: number[];
}

export interface SendFoundReportBundlePayload {
  items: SendFoundReportBundleItem[];
  recipientContactIds: number[];
  recipientCustoms: string[];
}

@Injectable({ providedIn: 'root' })
export class FoundReportPdfRepository {
  constructor(
    private readonly _restService: RestService,
    private readonly _toast: ToastService,
    private readonly _translateService: TranslocoService,
    private readonly _originRepository: AnimalOriginRepository,
    private readonly _whereaboutsRepository: AnimalWhereaboutsRepository
  ) {}

  /** POST /animal_origins/found_report_preview */
  getPreview(payload: FoundReportPreviewPayload): Observable<any | null> {
    return this._restService
      .httpPost('/animal_origins/found_report_preview', payload, {
        responseType: 'blob'
      })
      .pipe(
        take(1),
        // wenn du willst: success toast hier rauslassen – Preview soll eher still sein
        catchError(err => {
          console.error('Found report preview failed:', err);
          this._toast.showError(this._translateService.translate('animalAdd.toast.pdfPreviewError'));
          return of(null);
        })
      );
  }

  /** POST /animal_origins/securing_report_preview */
  getSecuringPreview(payload: SecuringReportPreviewPayload): Observable<any | null> {
    return this._restService
      .httpPost('/animal_origins/securing_report_preview', payload, {
        responseType: 'blob'
      })
      .pipe(
        take(1),
        catchError(err => {
          console.error('Securing report preview failed:', err);
          this._toast.showError(this._translateService.translate('animalAdd.toast.pdfPreviewError'));
          return of(null);
        })
      );
  }

  /** POST /animal_whereabouts/pickup_report_preview */
  getPickupPreview(payload: PickupReportPreviewPayload): Observable<Blob | null> {
    return this._restService
      .httpPost('/animal_whereabouts/pickup_report_preview', payload, {
        responseType: 'blob'
      })
      .pipe(
        take(1),
        catchError(err => {
          console.error('Pickup report preview failed:', err);
          this._toast.showError(this._translateService.translate('pickUpReport.toast.previewError'));
          return of(null);
        })
      );
  }

  /** POST /animal_whereabouts/{whereaboutId}/send_pickup_report_email */
  sendPickupReport(whereaboutId: number, payload: SendFoundReportPayload): Observable<any | null> {
    return this._restService
      .httpPost(`/animal_whereabouts/${whereaboutId}/send_pickup_report_email`, payload)
      .pipe(
        take(1),
        // Dashboard-Liste "PDF erstellt & noch nicht versendet" aktualisieren
        tap(() => this._whereaboutsRepository.refreshPendingReports()),
        catchError(err => {
          console.error('Send pickup report email failed:', err);
          this._toast.showError(
            this._translateService.translate('foundReport.send.toast.error')
          );
          return of(null);
        })
      );
  }

  send(originId: number, payload: SendFoundReportPayload): Observable<any | null> {
    return this._restService
      .httpPost(`/animal_origins/${originId}/send_found_report_email`, payload)
      .pipe(
        take(1),
        // Dashboard-Liste "PDF erstellt & noch nicht versendet" aktualisieren
        tap(() => this._originRepository.refreshPendingReports()),
        catchError(err => {
          console.error('Send found report email failed:', err);
          this._toast.showError(
            this._translateService.translate('foundReport.send.toast.error')
          );
          return of(null);
        })
      );
  }

  /** POST /animal_origins/{originId}/send_securing_report_email */
  sendSecuringReport(originId: number, payload: SendFoundReportPayload): Observable<any | null> {
    return this._restService
      .httpPost(`/animal_origins/${originId}/send_securing_report_email`, payload)
      .pipe(
        take(1),
        // Dashboard-Liste "PDF erstellt & noch nicht versendet" aktualisieren
        tap(() => this._originRepository.refreshPendingReports()),
        catchError(err => {
          console.error('Send securing report email failed:', err);
          this._toast.showError(
            this._translateService.translate('foundReport.send.toast.error')
          );
          return of(null);
        })
      );
  }

  /** POST /found_reports/send_bundle_email/ – eine Mail für mehrere Origins (Original + Duplikate). */
  sendBundle(payload: SendFoundReportBundlePayload): Observable<any | null> {
    return this._restService
      .httpPost('/found_reports/send_bundle_email', payload)
      .pipe(
        take(1),
        // Dashboard-Liste "PDF erstellt & noch nicht versendet" aktualisieren
        tap(() => this._originRepository.refreshPendingReports()),
        catchError(err => {
          console.error('Send found report bundle email failed:', err);
          this._toast.showError(
            this._translateService.translate('foundReport.send.toast.error')
          );
          return of(null);
        })
      );
  }
}